import { useMemo } from "react";
import {
  Check,
  Lock,
  Loader2,
  AlertTriangle,
  MessageCircle,
  Link2,
  Clock,
  Crown,
  Tag,
  User,
  ChevronDown,
  ChevronUp,
  Ban,
  FileText,
  Paperclip,
  Pencil,
  Trash2,
  Eye,
  FolderOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Ticket, formatTicketTypeLabel } from "@/types/ticket";
import { formatTitleCase, priorityBucket } from "@sd/lib/ticketStreamUtils";
import {
  formatStatusLabel,
  normalizeStatus,
  statusForSelect,
  AGENT_STATUS_CHANGE_OPTIONS,
} from "@sd/constants/ticketStatuses";
import { TicketStatusSelectItems } from "@sd/components/common/TicketStatusSelectItems";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AttachmentViewer,
  AttachmentBadge,
  SuperAgentAttachmentCards,
} from "./AttachmentViewer";
import { AutoCloseWarning } from "./AutoCloseWarning";
import { useAttachmentFilesByIds } from "../../hooks/useAttachmentFilesByIds";
import type { AdminTicket } from "@sd/types/admin";
import {
  assigneeDisplayName,
  ASSIGNED_SELECT_NONE,
  enrichAssigneeOptionsForTicket,
  resolveAssigneeSelectValue,
} from "@sd/lib/adminTicketUtils";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import { TicketDescriptionContent } from "@sd/components/common/TicketDescriptionContent";
import { formatTimeAgo } from "@sd/lib/ticketDetailActivity";
import type { TicketNoteEntry } from "@sd/lib/ticketNotesApi";

interface AssigneeOption {
  id: string;
  name: string;
}

interface TicketDetailProps {
  ticket: Ticket;
  onStatusChange: (status: string) => void;
  onPriorityChange: (priorityId: string) => void;
  priorityOptions?: { id: string; label: string }[];
  onExtendAutoClose?: () => void;
  onAssignTo?: (agentId: string, agentName: string) => void;
  assigneeOptions?: AssigneeOption[];
  onVipChange?: (isVip: boolean) => void;
  onSlaChange?: (isSla: boolean) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onOpenFullView?: () => void;
  videoPreviewControls?: boolean;
  agentAssigneeEditable?: boolean;
  assigneeOptionsLoading?: boolean;
  /** Agent / Super Agent workbench: Add comment button + API notes list. */
  showWorkbenchComments?: boolean;
  onOpenAddComment?: () => void;
  workbenchNotes?: TicketNoteEntry[];
  workbenchNotesLoading?: boolean;
  workbenchNotesError?: boolean;
  resolveCommentAuthor?: (raw: string | undefined | null) => string;
}

/** Reusable detailed section divider heading */
function DetailSectionHeader({
  label,
  icon: Icon,
}: {
  label: string;
  icon?: React.ElementType;
}) {
  return (
    <div className="mb-3 flex items-center gap-2 border-b border-border/60 pb-2">
      {Icon && <Icon className="h-4 w-4 text-slate-600" />}
      <h3 className="text-sm font-black uppercase tracking-wide text-slate-900">
        {label}
      </h3>
    </div>
  );
}

export function TicketDetail({
  ticket,
  onStatusChange,
  onPriorityChange,
  priorityOptions = [],
  onExtendAutoClose,
  onAssignTo,
  assigneeOptions = [],
  onVipChange,
  onSlaChange,
  onEdit,
  onDelete,
  onOpenFullView,
  videoPreviewControls = false,
  agentAssigneeEditable = false,
  assigneeOptionsLoading = false,
  showWorkbenchComments = false,
  onOpenAddComment,
  workbenchNotes = [],
  workbenchNotesLoading = false,
  workbenchNotesError = false,
  resolveCommentAuthor,
}: TicketDetailProps) {
  const { role } = useAuth();
  const { userDisplayMap } = useServiceDeskUserDisplayMap({
    refetchAgentListsOnMount: false,
  });
  const isAgentOnly = role === "ServiceDesk_Agent";
  const assigneeTicket = useMemo(
    (): AdminTicket =>
      ({
        assignedAgent: ticket.assignee ?? null,
      }) as AdminTicket,
    [ticket.assignee]
  );
  const resolvedAssigneeOptions = useMemo(
    () =>
      enrichAssigneeOptionsForTicket(
        assigneeOptions,
        assigneeTicket,
        userDisplayMap
      ),
    [assigneeOptions, assigneeTicket, userDisplayMap]
  );
  const assigneeSelectValue = useMemo(() => {
    const resolved = resolveAssigneeSelectValue(
      assigneeTicket,
      resolvedAssigneeOptions
    );
    return resolved === ASSIGNED_SELECT_NONE ? "__unassign__" : resolved;
  }, [assigneeTicket, resolvedAssigneeOptions]);
  const assigneeLabel = assigneeDisplayName(
    assigneeTicket,
    resolvedAssigneeOptions,
    userDisplayMap
  );
  const showCrudActions = Boolean(onEdit || onDelete || onOpenFullView);
  const showHeaderActions =
    showCrudActions || (showWorkbenchComments && onOpenAddComment);
  const priorityText = ticket.priorityLabel ?? ticket.priority;
  const useApiPriorityOptions = priorityOptions.length > 0;
  const canUseAssigneeSelect = !isAgentOnly || agentAssigneeEditable;

  const ticketTypeLabel = formatTicketTypeLabel(
    ticket.ticketType,
    ticket.ticketTypeRaw
  );

  const { attachments: fromApiFiles } = useAttachmentFilesByIds(
    ticket.attachmentFileIds
  );
  const combinedAttachments = useMemo(() => {
    const local = ticket.attachments ?? [];
    const seen = new Set(local.map(a => a.id));
    return [...local, ...fromApiFiles.filter(a => !seen.has(a.id))];
  }, [ticket.attachments, fromApiFiles]);

  const priorityDisplay =
    ticket.priorityLabel ??
    (ticket.priorityId && !ticket.priorityLabel ? "â€”" : ticket.priority);
  const priorityColorKey = priorityBucket(priorityDisplay || ticket.priority);

  return (
    <div className="@container/ticket-detail flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-r border-border bg-card max-xl:h-auto max-xl:shrink-0 max-xl:border-r-0">
      {/* Header â€” aligned with STREAM + UserPanel (h-14) */}
      <div className="grid h-14 min-h-14 shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-border bg-slate-100/50 px-3 lg:px-4">
        <div className="flex min-w-0 items-center gap-2 overflow-hidden">
          <span className="truncate font-mono text-xs font-bold tracking-wide text-slate-900">
            {ticket.uiId ?? ticket.id}
          </span>
          {ticketTypeLabel ? (
            <Badge
              variant="outline"
              className="shadow-3xs shrink-0 truncate rounded-md border-slate-300 bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-800"
            >
              {ticketTypeLabel}
            </Badge>
          ) : null}
        </div>
        {showHeaderActions ? (
          <div className="flex shrink-0 items-center gap-1">
            {showWorkbenchComments && onOpenAddComment ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-2 @[520px]/ticket-detail:px-2.5"
                title="Add comment"
                onClick={onOpenAddComment}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                <span className="hidden @[520px]/ticket-detail:inline">Add comment</span>
              </Button>
            ) : null}
            {onOpenFullView ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-2 @[520px]/ticket-detail:px-2.5"
                title="Full view"
                onClick={onOpenFullView}
              >
                <Eye className="h-3.5 w-3.5" />
                <span className="hidden @[520px]/ticket-detail:inline">Full view</span>
              </Button>
            ) : null}
            {onEdit ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-2 @[520px]/ticket-detail:px-2.5"
                title="Edit"
                onClick={onEdit}
              >
                <Pencil className="h-3.5 w-3.5" />
                <span className="hidden @[520px]/ticket-detail:inline">Edit</span>
              </Button>
            ) : null}
            {onDelete ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive @[520px]/ticket-detail:px-2.5"
                title="Delete"
                onClick={onDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span className="hidden @[520px]/ticket-detail:inline">Delete</span>
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <ScrollArea className="min-h-0 min-w-0 flex-1 max-xl:max-h-none max-xl:flex-none [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:!min-w-0 [&_[data-radix-scroll-area-viewport]>div]:!w-full">
        <div className="w-full min-w-0 max-w-full space-y-6 p-4 lg:p-5">
          {/* Main Title & Description Section */}
          <div className="min-w-0 max-w-full space-y-3">
            <h1 className="w-full min-w-0 max-w-full break-words text-xl font-bold leading-snug whitespace-normal text-slate-900 [overflow-wrap:anywhere]">
              {ticket.title}
            </h1>
            {ticket.description ? (
              <div className="shadow-2xs relative rounded-xl border border-border/80 bg-background/50 p-4">
                <span className="absolute bottom-0 left-0 top-0 w-1 rounded-l-xl bg-slate-300" />
                <TicketDescriptionContent
                  className="text-sm font-medium text-muted-foreground"
                  description={ticket.description
                    .replace(
                      /\s*\n*---\s*\n?ðŸ–¥ System Info[\s\S]*?---\s*$/m,
                      ""
                    )
                    .trim()}
                />
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground/60">
                No description provided.
              </p>
            )}
          </div>

          {/* Quick Actions & Metadata Toolbar */}
          <div className="shadow-2xs flex flex-wrap items-end gap-3 rounded-xl border border-border/80 bg-background p-3 @lg/ticket-detail:gap-4 @lg/ticket-detail:p-3.5">
            {/* Status Dropdown */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-900">
                Status
              </span>
              {isAgentOnly && normalizeStatus(ticket.status) === "close" ? (
                <div
                  title="Closed tickets cannot be changed"
                  className="flex h-9 w-full min-w-0 cursor-not-allowed select-none items-center gap-1.5 rounded-lg border border-red-200 bg-red-50/50 px-3 text-sm font-medium text-red-700"
                >
                  <Ban className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{formatStatusLabel(ticket.status)}</span>
                </div>
              ) : (
                <Select
                  key={`status-${ticket.id}-${statusForSelect(ticket.status)}`}
                  value={statusForSelect(ticket.status)}
                  onValueChange={onStatusChange}
                >
                  <SelectTrigger className="shadow-3xs h-9 w-full rounded-lg border-border/80 bg-background text-sm font-medium">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <TicketStatusSelectItems
                      options={
                        isAgentOnly ? AGENT_STATUS_CHANGE_OPTIONS : undefined
                      }
                    />
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Priority Dropdown */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-900">
                Priority
              </span>
              {isAgentOnly ? (
                <div
                  title="You don't have permission to change priority"
                  className={cn(
                    "flex h-9 w-full min-w-0 cursor-not-allowed select-none items-center gap-1.5 rounded-lg border px-3 text-sm font-medium",
                    priorityColorKey === "P1" &&
                      "border-destructive/20 bg-destructive/10 text-destructive",
                    priorityColorKey === "P2" &&
                      "border-warning/20 bg-warning/10 text-warning",
                    (priorityColorKey === "P3" || priorityColorKey === "P4") &&
                      "bg-secondary text-muted-foreground"
                  )}
                >
                  <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="min-w-0 truncate whitespace-nowrap">
                    {priorityDisplay}
                  </span>
                </div>
              ) : priorityOptions.length > 0 ? (
                <Select
                  key={`priority-${ticket.id}-${ticket.priorityId ?? ticket.priority}`}
                  value={ticket.priorityId ?? ""}
                  onValueChange={onPriorityChange}
                >
                  <SelectTrigger
                    className={cn(
                      "shadow-3xs h-9 w-full rounded-lg text-sm font-medium",
                      priorityColorKey === "P1" &&
                        "border-red-200 bg-red-50 text-red-700",
                      priorityColorKey === "P2" &&
                        "border-orange-200 bg-orange-50 text-orange-700",
                      (priorityColorKey === "P3" || priorityColorKey === "P4") &&
                        "border-border/80 bg-background"
                    )}
                  >
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityOptions.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={ticket.priority}
                  onValueChange={v => onPriorityChange(v)}
                >
                  <SelectTrigger
                    className={cn(
                      "shadow-3xs h-9 w-full rounded-lg text-sm font-medium",
                      ticket.priority === "P1" &&
                        "border-red-200 bg-red-50 text-red-700",
                      ticket.priority === "P2" &&
                        "border-orange-200 bg-orange-50 text-orange-700",
                      (ticket.priority === "P3" || ticket.priority === "P4") &&
                        "border-border/80 bg-background"
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P1">P1 Critical</SelectItem>
                    <SelectItem value="P2">P2 High</SelectItem>
                    <SelectItem value="P3">P3 Medium</SelectItem>
                    <SelectItem value="P4">P4 Low</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Assigned to Dropdown */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-900">
                Assigned to
              </span>
              {!canUseAssigneeSelect ? (
                <div
                  title="You don't have permission to reassign tickets"
                  className="flex h-9 w-full min-w-0 cursor-not-allowed select-none items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 text-sm font-medium text-muted-foreground"
                >
                  <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">
                    {assigneeLabel !== "Unassigned" ? assigneeLabel : "Unassigned"}
                  </span>
                </div>
              ) : assigneeOptionsLoading ? (
                <div
                  title="Loading agents"
                  className="flex h-9 w-full min-w-0 cursor-wait select-none items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 text-sm font-medium text-muted-foreground"
                >
                  <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />
                  <span className="truncate">Loading agentsâ€¦</span>
                </div>
              ) : (
                <Select
                  key={`assignee-${ticket.id}-${assigneeSelectValue}`}
                  value={assigneeSelectValue}
                  onValueChange={v => {
                    if (v === "__unassign__") {
                      onAssignTo?.("__unassign__", "");
                      return;
                    }
                    const opt = resolvedAssigneeOptions.find(a => a.id === v);
                    if (opt) onAssignTo?.(opt.id, opt.name);
                  }}
                >
                  <SelectTrigger className="shadow-3xs h-9 w-full rounded-lg border-dashed border-border/80 bg-background text-sm font-medium">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassign__">Unassigned</SelectItem>
                    {resolvedAssigneeOptions.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {onSlaChange !== undefined && (
              <div className="flex flex-col gap-1">
                <span className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-900">
                  SLA On
                </span>
                <div
                  className={cn(
                    "shadow-3xs flex h-9 w-full min-w-[7rem] items-center gap-2 rounded-lg border bg-background px-3 transition-colors",
                    ticket.isSla
                      ? "border-blue-300 bg-blue-50/50"
                      : "border-border/80"
                  )}
                >
                  <Clock
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 transition-colors",
                      ticket.isSla
                        ? "text-blue-500"
                        : "text-muted-foreground"
                    )}
                  />
                  <span
                    className={cn(
                      "text-xs font-bold transition-colors",
                      ticket.isSla ? "text-blue-700" : "text-muted-foreground"
                    )}
                  >
                    SLA
                  </span>
                  <Switch
                    checked={ticket.isSla ?? false}
                    className="scale-90 data-[state=checked]:bg-blue-500 data-[state=unchecked]:bg-input"
                    onCheckedChange={onSlaChange}
                  />
                </div>
              </div>
            )}

            {onVipChange !== undefined && (
              <div className="flex flex-col gap-1">
                <span className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-900">
                  VIP Status
                </span>
                <div
                  className={cn(
                    "shadow-3xs flex h-9 w-full min-w-[7rem] items-center gap-2 rounded-lg border bg-background px-3 transition-colors",
                    ticket.isVip
                      ? "border-amber-300 bg-amber-50/50"
                      : "border-border/80"
                  )}
                >
                  <Crown
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 transition-colors",
                      ticket.isVip
                        ? "fill-amber-500/20 text-amber-500"
                        : "text-muted-foreground"
                    )}
                  />
                  <span
                    className={cn(
                      "text-xs font-bold transition-colors",
                      ticket.isVip ? "text-amber-700" : "text-muted-foreground"
                    )}
                  >
                    VIP
                  </span>
                  <Switch
                    checked={ticket.isVip ?? false}
                    className="scale-90 data-[state=checked]:bg-amber-500 data-[state=unchecked]:bg-input"
                    onCheckedChange={onVipChange}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Categories & Badges Section */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {ticket.projectName && (
              <Badge
                variant="secondary"
                className="gap-1 rounded-md border-transparent bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700"
              >
                <FolderOpen className="h-3 w-3" />
                {ticket.projectName}
              </Badge>
            )}
            {!isAgentOnly && ticket.changeRequest?.crNumber ? (
              <Badge
                variant="secondary"
                className="gap-1 rounded-md border-transparent bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700"
              >
                <FileText className="h-3 w-3" />
                {ticket.changeRequest.crNumber}
              </Badge>
            ) : null}
            {ticket.category && (
              <Badge
                variant="secondary"
                className="gap-1 rounded-md border-transparent bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700"
              >
                <Tag className="h-3 w-3" />
                {formatTitleCase(ticket.category)}
                {ticket.subcategory &&
                  ` / ${formatTitleCase(ticket.subcategory)}`}
              </Badge>
            )}
            {ticket.relatedTicketIds && ticket.relatedTicketIds.length > 0 && (
              <Badge
                variant="outline"
                className="gap-1 rounded-md px-2.5 py-1 text-xs font-bold"
              >
                <Link2 className="h-3 w-3" />
                {ticket.relatedTicketIds.length} related
              </Badge>
            )}
          </div>

          {/* Auto-Close Warning */}
          {normalizeStatus(ticket.status) === "ready_for_qa" && ticket.autoCloseAt && (
            <AutoCloseWarning
              autoCloseAt={ticket.autoCloseAt}
              ticketStatus={ticket.status}
              onExtend={onExtendAutoClose}
            />
          )}

          {/* Resolution Details */}
          {ticket.resolution && (
            <div className="shadow-xs space-y-2 rounded-xl border border-success/30 bg-success/5 p-4">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-success/15">
                  <Check className="h-3.5 w-3.5 text-success" />
                </div>
                <span className="text-sm font-black text-success">
                  Resolution Details
                </span>
                {ticket.resolution.slaBreach && (
                  <Badge
                    variant="outline"
                    className="border-warning/30 bg-warning/5 text-xs font-bold text-warning"
                  >
                    SLA Breach
                  </Badge>
                )}
              </div>
              <p className="text-sm font-semibold leading-relaxed text-foreground">
                {ticket.resolution.notes}
              </p>
              <p className="text-xs font-semibold text-muted-foreground/75">
                {ticket.resolution.resolvedBy} â€¢ {ticket.resolution.code}
              </p>
            </div>
          )}

          {/* Notes Section */}
          {ticket.notes.length > 0 && (
            <div className="space-y-4">
              <DetailSectionHeader
                label="Replies & Activity"
                icon={MessageCircle}
              />
              <div className="space-y-3">
                {ticket.notes.map(note => (
                  <div
                    key={note.id}
                    className={cn(
                      "shadow-3xs rounded-xl border border-border/80 p-4",
                      note.type === "private"
                        ? "border-l-4 border-l-amber-500 bg-amber-50/40"
                        : "border-l-4 border-l-slate-400 bg-slate-50/40"
                    )}
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        {note.type === "private" ? (
                          <>
                            <Lock className="h-3.5 w-3.5 text-amber-600" />
                            <span className="text-xs font-black uppercase tracking-wider text-amber-700">
                              Private Note
                            </span>
                          </>
                        ) : (
                          <>
                            <MessageCircle className="h-3.5 w-3.5 text-slate-600" />
                            <span className="text-xs font-black uppercase tracking-wider text-slate-800">
                              Public Reply
                            </span>
                          </>
                        )}
                      </div>
                      {note.attachments && note.attachments.length > 0 && (
                        <AttachmentBadge count={note.attachments.length} />
                      )}
                    </div>
                    <p className="mb-3 text-sm font-semibold leading-relaxed text-foreground/95">
                      {note.content}
                    </p>
                    {note.attachments && note.attachments.length > 0 && (
                      <AttachmentViewer attachments={note.attachments} />
                    )}
                    <div className="mt-1 flex items-center justify-between border-t border-border/40 pt-2 text-xs font-semibold text-muted-foreground/70">
                      <span>{note.author}</span>
                      <span>{note.timestamp}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SLA Warning */}
          {ticket.slaTimers.some(t => t.isBreached) && (
            <div className="flex items-center gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-3.5">
              <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
              <div>
                <span className="text-sm font-bold text-foreground">
                  SLA Breach Detected
                </span>
                <p className="mt-0.5 text-xs text-muted-foreground/80">
                  Consider escalation or immediate manager review.
                </p>
              </div>
            </div>
          )}

          {/* Attachments Section */}
          {combinedAttachments.length > 0 && (
            <div className="space-y-4 pt-2">
              <DetailSectionHeader label="Attachments" icon={Paperclip} />
              <div className="shadow-3xs rounded-xl border border-border/80 bg-background/50 p-4">
                <SuperAgentAttachmentCards
                  attachments={combinedAttachments}
                  variant="embedded"
                  videoShowPlayStopBar={videoPreviewControls}
                />
              </div>
            </div>
          )}

          {showWorkbenchComments ? (
            <div className="space-y-4 pt-2">
              <DetailSectionHeader label="Comments" icon={MessageCircle} />
              {workbenchNotesLoading ? (
                <p className="text-sm text-muted-foreground">Loading commentsâ€¦</p>
              ) : workbenchNotesError ? (
                <p className="text-sm text-destructive">
                  Could not load comments.
                </p>
              ) : workbenchNotes.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
                  No comments yet. Use Add comment above to write one.
                </p>
              ) : (
                <ul className="space-y-3">
                  {workbenchNotes.map((note, index) => {
                    const author = resolveCommentAuthor
                      ? resolveCommentAuthor(note.createdBy)
                      : note.createdBy;
                    const when = formatTimeAgo(note.createdOn);
                    return (
                      <li
                        key={note.id}
                        className="shadow-3xs rounded-xl border border-border/80 border-l-4 border-l-sky-400 bg-slate-50/40 p-4"
                      >
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <MessageCircle className="h-3.5 w-3.5 shrink-0 text-sky-600" />
                            <span className="truncate text-xs font-black uppercase tracking-wider text-sky-800">
                              {author}
                            </span>
                          </div>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {when ? `#${index + 1} Â· ${when}` : `#${index + 1}`}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed text-foreground/95">
                          {note.note}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}
